<template>
  <div class="create_wrapper">
    <!-- Start Breadcrumb -->
    <Breadcrumb :items="items" />
    <!-- End Breadcrumb -->

    <div class="custom_card">
      <div class="card-header">
        <h4 class="card-title">{{ $t("edit") }}</h4>
      </div>

      <MainLoader v-if="loading" />

      <!-- ==== Start Form ==== -->
      <form @submit.prevent="validateEditForm" v-else>
        <div class="container">
          <div class="row justify-content-start">
            <!-- Arabic Name -->
            <div class="col-lg-4 py-0">
              <div class="input_wrapper top_label">
                <input
                  @keypress="isArabic"
                  type="text"
                  class="form-control"
                  v-model.trim="data.ar.name"
                  id="name_ar_input"
                />
                <label for="name_ar_input" class="form-label">
                  {{ $t("forms.labels.name_ar") }}*
                </label>
              </div>
            </div>

            <!-- English Name -->
            <div class="col-lg-4 py-0">
              <div class="input_wrapper top_label">
                <input
                  @keypress="isEnglish"
                  type="text"
                  class="form-control"
                  v-model.trim="data.en.name"
                  id="name_en_input"
                />
                <label for="name_en_input" class="form-label">
                  {{ $t("forms.labels.name_en") }}*
                </label>
              </div>
            </div>

            <!-- City Selection -->
            <div class="col-lg-4 py-0">
              <div class="input_wrapper top_label">
                <label class="form-label">
                  {{ $t("forms.labels.city") }}*
                </label>
                <multiselect
                  v-model="data.city"
                  :options="cities"
                  label="name"
                  track-by="id"
                  placeholder=" "
                  :searchable="true"
                  :allow-empty="false"
                  :show-labels="false"
                ></multiselect>
              </div>
            </div>

            <!-- Location Description -->
            <div class="col-lg-4 py-0">
              <div class="input_wrapper top_label">
                <input
                  type="text"
                  class="form-control"
                  v-model.trim="data.location"
                  id="location_input"
                />
                <label for="location_input" class="form-label">
                  {{ $t("forms.labels.location_description") }}
                </label>
              </div>
            </div>

            <!-- Delivery Price -->
            <div class="col-lg-4 py-0">
              <div class="input_wrapper top_label">
                <input
                  type="number"
                  class="form-control"
                  @input="
                    helper_checkIfInputIsEmpty;
                    addDataLocalStorage();
                  "
                  v-model.trim="data.delivery_price"
                  id="delivery_price_input"
                />
                <label for="delivery_price_input" class="form-label">
                  {{ $t("forms.labels.delivery_price") }}*
                </label>
              </div>
            </div>

            <!-- Is Active -->
            <div class="col-lg-4 py-0 d-flex align-items-center">
              <v-checkbox
                :label="$t('forms.labels.is_active')"
                v-model="data.is_active"
                color="#783D73"
              ></v-checkbox>
            </div>

            <!-- Interactive Polygon Map Picker -->
            <div class="col-lg-12 py-3">
              <div class="input_wrapper top_label">
                <label class="form-label font-weight-bold mb-2">
                  <i class="fas fa-map-marked-alt text-primary me-1"></i>
                  {{ $t("forms.labels.area") }}*
                </label>
                <district-map-picker
                  :initial-area="data.area"
                  :initial-center="data.center"
                  height="500px"
                  @area-changed="data.area = $event"
                  @center-changed="data.center = $event"
                  @location-changed="onLocationResolved"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="buttons_wrapper">
          <button
            type="submit"
            class="button_style_1"
            :class="btnIsLoading ? 'disabled' : ''"
          >
            {{ $t("forms.submit") }}
            <span class="btn_loader" v-if="btnIsLoading"></span>
          </button>
        </div>
      </form>
      <!-- ==== End Form ==== -->
    </div>
  </div>
</template>

<script>
export default {
  name: "DistrictUpdate",

  props: ["id"],

  data() {
    return {
      // ========== Breadcrumbs
      items: [
        {
          text: this.$t("breadcrumb.mainPage"),
          disabled: false,
          href: "/",
        },
        {
          text: this.$t("breadcrumb.districts.title"),
          disabled: false,
          href: "/district/show-all",
        },
        {
          text: this.$t("breadcrumb.districts.edit"),
          disabled: true,
          href: "",
        },
      ],

      // ========== Loading
      btnIsLoading: false,
      loading: false,

      // ========== Form data
      data: {
        ar: {
          name: null,
        },
        en: {
          name: null,
        },
        city: null,
        location: null,
        is_active: true,
        center: { lat: null, lng: null },
        area: [],
      },

      // Fixed Data
      cities: [],
    };
  },

  methods: {
    onLocationResolved(loc) {
      if (!this.data.location) {
        this.data.location = loc;
      }
    },

    // Validate Data
    validateEditForm() {
      this.btnIsLoading = true;

      if (!this.data.ar.name) {
        this.$iziToast.error({
          timeout: 2500,
          message: this.$t("forms.validation.name_ar"),
          position: "bottomRight",
        });
        this.btnIsLoading = false;
        return;
      }

      if (!this.data.en.name) {
        this.$iziToast.error({
          timeout: 2500,
          message: this.$t("forms.validation.name_en"),
          position: "bottomRight",
        });
        this.btnIsLoading = false;
        return;
      }

      if (!this.data.city) {
        this.$iziToast.error({
          timeout: 2500,
          message: this.$t("forms.validation.city"),
          position: "bottomRight",
        });
        this.btnIsLoading = false;
        return;
      }

      if (!this.data.delivery_price) {
        this.$iziToast.error({
          timeout: 2500,
          message: this.$t("forms.validation.delivery_price"),
          position: "bottomRight",
        });
        this.btnIsLoading = false;
        return;
      }

      if (!this.data.area || this.data.area.length < 4) {
        this.$iziToast.error({
          timeout: 3000,
          message: this.$t("forms.validation.district_area_min"),
          position: "bottomRight",
        });
        this.btnIsLoading = false;
        return;
      }

      if (!this.data.center || !this.data.center.lat || !this.data.center.lng) {
        let sumLat = 0;
        let sumLng = 0;
        this.data.area.forEach((p) => {
          sumLat += parseFloat(p.lat);
          sumLng += parseFloat(p.lng);
        });
        this.data.center = {
          lat: sumLat / this.data.area.length,
          lng: sumLng / this.data.area.length,
        };
      }

      this.submitData();
    },

    // Submit Data
    submitData() {
      const payload = {
        city_id: this.data.city?.id,
        lat: String(this.data.center.lat),
        lng: String(this.data.center.lng),
        location: this.data.location || "",
        is_active: this.data.is_active ? 1 : 0,
        delivery_price: this.data.delivery_price,
        area: this.data.area.map((p) => ({
          lat: parseFloat(p.lat),
          lng: parseFloat(p.lng),
        })),
        ar: {
          name: this.data.ar.name,
        },
        en: {
          name: this.data.en.name,
        },
        _method: "PUT",
      };

      this.$axios({
        method: "POST",
        url: `district/${this.id}`,
        data: payload,
      })
        .then(() => {
          this.$iziToast.success({
            timeout: 2000,
            message: this.$t("editSuccess"),
            position: "bottomRight",
          });
          this.$router.push({ path: "/district/show-all" });
          this.btnIsLoading = false;
        })
        .catch((err) => {
          this.$iziToast.error({
            timeout: 3000,
            message: err.response?.data?.message || "Error updating district",
            position: "bottomRight",
          });
          this.btnIsLoading = false;
        });
    },

    // Get Data
    getData() {
      this.loading = true;

      // Load Cities
      const citiesPromise = this.$axios({
        method: "GET",
        url: "city_not_paginated",
      }).then((res) => {
        this.cities = res.data.data.map((item) => ({
          id: item.id,
          name: item.name,
        }));
      });

      // Load District Details
      const districtPromise = this.$axios({
        method: "GET",
        url: `district/${this.id}`,
      }).then((res) => {
        const item = res.data.data;
        this.data.ar.name = item.ar?.name || item.name;
        this.data.en.name = item.en?.name || item.name;
        this.data.is_active = Boolean(item.is_active);
        this.data.delivery_price = item.delivery_price;
        this.data.location = item.location;

        if (item.lat && item.lng) {
          this.data.center = {
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lng),
          };
        }

        if (item.city) {
          this.data.city = {
            id: item.city.id,
            name: item.city.name,
          };
        }

        if (Array.isArray(item.area) && item.area.length > 0) {
          this.data.area = item.area.map((p) => ({
            lat: parseFloat(p.lat),
            lng: parseFloat(p.lng),
          }));
        }
      });

      Promise.all([citiesPromise, districtPromise])
        .then(() => {
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          this.$iziToast.error({
            timeout: 3000,
            message: err.response?.data?.message || "Error loading district",
            position: "bottomRight",
          });
        });
    },
  },

  created() {
    this.getData();
  },
};
</script>

<style lang="scss" scoped></style>
